import { ReactElement, useMemo } from 'react'
import { Drawer2D, useTapelineCentersSnapTargets, useTapelineSnapTargets } from '@scene'
import { v4 as uuid } from 'uuid'
import { useTheme } from '@mui/material'
import { DEFAULT_WALL_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useEditElementStore, useModelStore } from '@editorStores'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { round2DCoordinates } from 'src/geometry/rounding'
import { useModelSnapTargets } from '../../hooks'
import { NUM_DECIMALS_ROUND_WALL_DRAWER_2D } from './constants'

interface Props {
  activeStorey: string
  /**
   * Called with the freshly drawn wall instead of the default behaviour (add a local wall to the
   * store and select it). Lets callers persist the wall differently — e.g. the IFC importer creates
   * it on the backend right away.
   */
  onWallDrawn?: (wall: Wall) => void
}

const minWallLength = 0.1

const WallDrawer2D = ({ activeStorey, onWallDrawn }: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const addWall = useModelStore(state => state.addWall)
  const { storey_boundaries } = useModelStore(state => state.model)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const modelSnapTargets = useModelSnapTargets({ xyOnly: true })
  const tapelineTargets = useTapelineSnapTargets(true)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(true)

  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineTargets, tapelineCenterTargets],
  )

  // EFFECTS
  useDefault2DSnapConfigForElementType(SnapTargetElementType.WALLS)

  return (
    <>
      <Drawer2D
        minLength={minWallLength}
        additionalSnapTargets={additionalSnapTargets}
        color={scenePalette.elements2d.internalWalls.color}
        onDrawStart={() => setActiveElement(null)}
        onDrawEnd={(start, end) => {
          const startRounded = round2DCoordinates(
            { x: start.x, y: start.y },
            NUM_DECIMALS_ROUND_WALL_DRAWER_2D,
          )
          const endRounded = round2DCoordinates(
            { x: end.x, y: end.y },
            NUM_DECIMALS_ROUND_WALL_DRAWER_2D,
          )
          const [lowerBoundary, upperBoundary] = storey_boundaries[activeStorey]

          const points = [
            new ImmutableVector3(startRounded.x, startRounded.y, lowerBoundary),
            new ImmutableVector3(endRounded.x, endRounded.y, lowerBoundary),
            new ImmutableVector3(endRounded.x, endRounded.y, upperBoundary),
            new ImmutableVector3(startRounded.x, startRounded.y, upperBoundary),
          ]

          const wall: Wall = {
            ...DEFAULT_WALL_SETTINGS,
            shape: { points },
            placement: 'Internal' as WallPlacement,
            guid: uuid(),
            storey: activeStorey,
            is_local: true,
            element_type: 'inner_walls' as ElementTypes,
          }

          if (onWallDrawn) {
            onWallDrawn(wall)
            return
          }

          addWall(wall)
          setActiveElement(wall.guid)
        }}
      />
    </>
  )
}

export default WallDrawer2D
